<template>
  <div class="company-details-wrapper">
    <SectionScrollable :items="items">
      <template #billing>
        <div class="company-details-section-title">
          <AwIcon name="i-ph-receipt-fill" />
          Informations de facturation
          <CompanyUpdateModal :company="company" @on-update="onUpdate" />
        </div>
        <div style="background-color: var(--gray-5); height: 1px; width: 100%; margin-top: 10px"></div>
        <div class="mt-1">
          <DetailsCard
            label="Adresse de facturation"
            :value="company.billing_address?.formatted ?? '-'"
            :copy-text="company.billing_address?.formatted ?? undefined"
          />
        </div>
        <div class="flex flex-row gap-2 w-full">
          <DetailsCard
            label="Mode de règlement"
            :value="company.payment_method ? translatePaymentMethod.fr[company.payment_method] : '-'"
          />
          <DetailsCard
            label="Statut d'affacturage"
            :value="company.factoring_status ? translateFactoringStatus.fr[company.factoring_status] : '-'"
          />
        </div>
        <DetailsCard
          label="Processus de facturation"
          :value="company.billing_process ?? '-'"
          :copy-text="company.billing_process ?? undefined"
        />
      </template>
      <template #information>
        <div class="company-details-section-title">
          <AwIcon name="i-ph-file-text-fill" />
          Informations de contact
        </div>
        <div style="background-color: var(--gray-5); height: 1px; width: 100%; margin-top: 10px"></div>

        <div style="display: flex; flex-direction: column; margin-top: 10px; gap: 22px">
          <div style="display: flex; flex-direction: column; gap: 12px">
            <div class="contact-list-title">
              <AwIcon name="i-ph-user-circle-check-duotone" class="font-size-4" />
              Contacts décisionnaires
            </div>
            <CompanyDetailsContactList
              :contacts="decisionMakerContacts"
              empty-message="Aucun contact décisionnaire trouvé"
              :loading="loading"
            />
          </div>

          <div style="display: flex; flex-direction: column; gap: 12px">
            <div class="contact-list-title">
              <AwIcon name="i-ph-user-square-duotone" class="font-size-4" />
              Contacts comptabilité
            </div>
            <CompanyDetailsContactList
              :contacts="accountingContacts"
              empty-message="Aucun contact comptabilité trouvé"
              :loading="loading"
            />
          </div>
          <div style="display: flex; flex-direction: column; gap: 12px">
            <div class="contact-list-title">
              <AwIcon name="i-ph-user-circle-gear-duotone" class="font-size-4" />
              Contacts administratifs
            </div>
            <CompanyDetailsContactList
              :contacts="adminContacts"
              empty-message="Aucun contact administratif trouvé"
              :loading="loading"
            />
          </div>
          <div style="display: flex; flex-direction: column; gap: 12px">
            <div class="contact-list-title">
              <AwIcon name="i-ph-user-switch-duotone" class="font-size-4" />
              Contacts recouvrement
            </div>
            <CompanyDetailsContactList
              :contacts="recoveryContacts"
              empty-message="Aucun contact recouvrement trouvé"
              :loading="loading"
            />
          </div>
        </div>
      </template>
      <template #remuneration>
        <CompanyDetailsRemuneration :company="company" />
      </template>
    </SectionScrollable>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "~/composables/useApi";
import { useEnvironment } from "~/composables/useEnvironment";
import { usePosthog } from "~/composables/usePosthog";
import type { Company } from "@asap/shared/schemas/company";
import type { Contact } from "@asap/shared/schemas/contact";
import type { ContractContactType } from "@asap/shared/utils/supabase.enum";
import { translatePaymentMethod, translateFactoringStatus } from "@asap/shared/utils/translate";

const api = useApi();
const posthog = usePosthog();
const { isProduction } = useEnvironment();

const props = defineProps<{
  company: Company;
}>();

const loading = ref(true);
const showRemunerationSection = ref(false);
const adminContacts = ref<Contact[]>([]);
const recoveryContacts = ref<Contact[]>([]);
const company = ref<Company>(props.company);
const accountingContacts = ref<Contact[]>([]);
const decisionMakerContacts = ref<Contact[]>([]);

const getContacts = async (type: ContractContactType) => {
  const response = await api.invoke({
    path: "contacts",
    method: "GET",
    query: {
      companyId: props.company.id,
      type,
    },
  });

  if (!response.success) {
    throw new Error(response.error.message);
  }

  return response.data.items;
};

const onUpdate = (updatedCompany: Company) => {
  company.value = updatedCompany;
};

onMounted(async () => {
  if (isProduction) {
    showRemunerationSection.value = (await posthog?.isFeatureFlagEnabled("remuneration-elements")) ?? false;
  } else {
    showRemunerationSection.value = true;
  }
  adminContacts.value = await getContacts("admin");
  accountingContacts.value = await getContacts("accounting");
  decisionMakerContacts.value = await getContacts("decision_maker");
  recoveryContacts.value = await getContacts("recovery");
  loading.value = false;
});

const baseSectionItems = [
  { label: "Informations de facturation", icon: "i-ph-receipt-fill", slot: "billing" },
  { label: "Informations de contact", icon: "i-ph-file-text-fill", slot: "information" },
];

const remunerationSectionItem = {
  label: "Paramètres de la société",
  icon: "i-ph-building-duotone",
  slot: "remuneration",
} as const;

const items = computed(() =>
  showRemunerationSection.value ? [...baseSectionItems, remunerationSectionItem] : baseSectionItems
);
</script>

<style scoped lang="scss">
.company-details-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.company-details-section-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 16px;
  font-weight: 900;
}

.contact-list-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 500;
}
</style>
